import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { ApiSuccessEnvelope } from "../../types/affiliates";
import {
  getAnalyticsInstance,
  type AffiliateAnalyticsDataSource,
} from "./analyticsDataSource";
import type { AffiliateAnalyticsFilters } from "./useAffiliateKpiCards";

export interface AffiliateChartRange {
  start_date: string;
  end_date: string;
}

// Daily new users + FTDs point, already mapped to the chart's field names.
export interface AffiliateDailyPoint {
  date: string;
  users: number;
  ftds: number;
}

interface AffiliateTimeSeriesData {
  items?: Array<{
    day?: string;
    total_users?: number;
    total_ftds?: number;
  }>;
}

const buildParams = (
  range: AffiliateChartRange,
  filters: AffiliateAnalyticsFilters,
) => {
  const params: Record<string, string> = {
    start_date: range.start_date,
    end_date: range.end_date,
  };
  if (filters.affiliate) params.affiliate = filters.affiliate;
  if (filters.promo_code) params.promo_code = filters.promo_code;
  return params;
};

const fetchTimeSeries = async (
  route: string,
  range: AffiliateChartRange,
  filters: AffiliateAnalyticsFilters,
  dataSource: AffiliateAnalyticsDataSource,
): Promise<AffiliateDailyPoint[]> => {
  const response = await getAnalyticsInstance(dataSource).get<
    ApiSuccessEnvelope<AffiliateTimeSeriesData>
  >(route, { params: buildParams(range, filters) });
  return (response.data.data?.items ?? []).map((item) => ({
    date: item.day ?? "",
    users: item.total_users ?? 0,
    ftds: item.total_ftds ?? 0,
  }));
};

const resolveTimeSeriesRoute = (
  dataSource: AffiliateAnalyticsDataSource,
  affiliateId?: string,
): string => {
  // Portal routes carry no affiliate_id — the token scopes the query.
  if (dataSource === "portal") {
    return API_ROUTES.AFFILIATE_PORTAL_ANALYTICS_NEW_USERS_FTDS;
  }
  return affiliateId
    ? API_ROUTES.AFFILIATE_DETAIL_ANALYTICS_NEW_USERS_FTDS.replace(
        "{affiliate_id}",
        encodeURIComponent(affiliateId),
      )
    : API_ROUTES.AFFILIATE_ANALYTICS_NEW_USERS_FTDS;
};

// Daily new-users-and-FTDs series. `affiliateId` present → per-affiliate
// endpoint; absent → the module-wide series. `options.enabled: false` skips
// the fetch — for previews where the module fallback would be wrong.
export const useAffiliateDailyUsersFtds = (
  range: AffiliateChartRange,
  affiliateId?: string,
  filters: AffiliateAnalyticsFilters = {},
  dataSource: AffiliateAnalyticsDataSource = "operator",
  options: { enabled?: boolean } = {},
): UseQueryResult<AffiliateDailyPoint[], Error> =>
  useQuery<AffiliateDailyPoint[], Error>({
    queryKey: [
      "affiliate-kpis",
      "new-users-ftds",
      dataSource,
      affiliateId ?? "",
      range.start_date,
      range.end_date,
      filters.affiliate ?? null,
      filters.promo_code ?? null,
    ],
    queryFn: () =>
      fetchTimeSeries(
        resolveTimeSeriesRoute(dataSource, affiliateId),
        range,
        filters,
        dataSource,
      ),
    enabled:
      (options.enabled ?? true) && Boolean(range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

export { fetchTimeSeries };
